import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import CodeOrderCard from '../components/CodeOrderCard'
import { formatDate, todayISO } from '../lib/format'
import { useEscClose } from '../lib/useEscClose'
import type { CodeOrder } from '../types'

/**
 * Заказы кодов маркировки.
 *
 * Раздел живёт у клиентов с включённым расширенным учётом. У остальных его нет
 * вовсе: сервер не отдаст ни список, ни карточку.
 *
 * Отдельно от заявок намеренно. Заявка — это разговор с клиентом, а здесь
 * поставка: двадцать грузовиков, семьдесят позиций, полтысячи паллет. Такое в
 * переписку не укладывается — это надо раскладывать деревом.
 */
export default function CodeOrders() {
  const { can } = useAuth()
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // Поставка, которую собираются удалить. Дело необратимое, поэтому не
  // confirm() на бегу, а окно с набором названия.
  const [удаляем, setУдаляем] = useState<CodeOrder | null>(null)

  const list = useQuery({
    queryKey: ['code-orders'],
    queryFn: () => api.codeOrders(),
    refetchInterval: 60_000,
  })

  const rows = list.data ?? []

  return (
    <>
      <h1 className="page-title">Заказы кодов</h1>

      <div className="toolbar">
        {can('tickets.reply') && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            + Новая поставка
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>Всего: {rows.length}</div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <th>Клиент</th>
              <th>Дата</th>
              <th>Поставка</th>
              <th>Этап</th>
              <th className="num">Грузовиков</th>
              <th className="num">Позиций</th>
              <th className="num">Паллет</th>
              <th className="num">Единиц</th>
              <th>Обновлено</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={11} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="empty">
                  Заказов кодов нет. Раздел работает только у клиентов, которым
                  в карточке включён расширенный учёт кодов.
                </td>
              </tr>
            )}
            {rows.map((o) => (
              <tr key={o.id}>
                <td className="task-num">{o.number || o.id}</td>
                <td className="cell-client">
                  <span className="client-name">{o.client_name}</span>
                </td>
                <td className="nowrap">{formatDate(o.supply_date)}</td>
                <td>
                  <button className="link-btn" onClick={() => setOpenId(o.id)}>
                    {o.title}
                  </button>
                  {o.from_client && <span className="cell-sub">из кабинета клиента</span>}
                </td>
                <td className="nowrap">
                  <span className={`co-chip co-${o.stage}`}>{o.stage_title}</span>
                </td>
                <td className="num">{o.trucks_count || '—'}</td>
                <td className="num">{o.items_count || '—'}</td>
                <td className="num">{o.pallets_count || '—'}</td>
                <td className="num">{o.units_total ? o.units_total.toLocaleString('ru') : '—'}</td>
                <td className="nowrap">{formatDate(o.updated_at)}</td>
                <td className="nowrap">
                  {can('tickets.reply') && (
                    <button
                      className="link-btn danger"
                      onClick={() => setУдаляем(o)}
                      title="Удалить поставку вместе с деревом и файлами"
                    >
                      удалить
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewOrderDialog onClose={() => setCreating(false)} onCreated={setOpenId} />}
      {удаляем && (
        <DeleteDialog order={удаляем} onClose={() => setУдаляем(null)} />
      )}
      {openId !== null && <CodeOrderCard id={openId} onClose={() => setOpenId(null)} />}
    </>
  )
}

/**
 * Удаление поставки.
 *
 * Требуем набрать название. В списке поставки похожи друг на друга —
 * «Поставка 27.08.26», «Поставка 02.09.26», — и промахнуться строкой проще,
 * чем кажется, а вместе с поставкой уходят дерево, коды и документы.
 */
function DeleteDialog({ order, onClose }: { order: CodeOrder; onClose: () => void }) {
  const qc = useQueryClient()
  const [набрано, setНабрано] = useState('')
  const [error, setError] = useState('')

  useEscClose(onClose)

  const удалить = useMutation({
    mutationFn: () => api.deleteCodeOrder(order.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['code-orders'] })
      onClose()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить'),
  })

  const совпало = набрано.trim() === order.title.trim()

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 520 }}
        onSubmit={(e) => {
          e.preventDefault()
          if (совпало) удалить.mutate()
        }}
      >
        <h2>Удалить поставку?</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <div className="warn">
            Вместе с поставкой «{order.title}» уйдут {order.trucks_count} грузовиков,{' '}
            {order.pallets_count} паллет и все загруженные файлы — коды, документы
            агрегации, декларации. Вернуть это будет нельзя.
          </div>
          <div className="field full">
            <label htmlFor="del-title">
              Наберите название поставки, чтобы подтвердить
            </label>
            <input
              id="del-title"
              value={набрано}
              onChange={(e) => setНабрано(e.target.value)}
              placeholder={order.title}
              autoFocus
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn danger" disabled={!совпало || удалить.isPending}>
            {удалить.isPending ? 'Удаляем…' : 'Удалить насовсем'}
          </button>
        </div>
      </form>
    </div>
  )
}

function NewOrderDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const qc = useQueryClient()
  const [clientId, setClientId] = useState('')
  const [title, setTitle] = useState('')
  const [дата, setДата] = useState(todayISO())
  const [error, setError] = useState('')

  useEscClose(onClose)

  // Только клиенты с включённым расширенным учётом: остальным сервер откажет,
  // и предлагать их в списке — значит обещать то, чего не будет.
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const годные = (clients.data ?? []).filter((c) => (c.features ?? []).includes('codes_tree'))

  const create = useMutation({
    mutationFn: () =>
      api.createCodeOrder({
        client_id: Number(clientId),
        title: title.trim(),
        supply_date: дата,
      }),
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ['code-orders'] })
      onClose()
      onCreated(o.id)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось создать'),
  })

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 520 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          create.mutate()
        }}
      >
        <h2>Новая поставка</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}

          {годные.length === 0 ? (
            <div className="warn">
              Ни одному клиенту не включён расширенный учёт кодов. Включается в
              карточке клиента: Справочники → Клиенты → Особые возможности.
            </div>
          ) : (
            <>
              <div className="field full field-accent">
                <label htmlFor="co-client">Клиент</label>
                <select
                  id="co-client"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  required
                  autoFocus
                >
                  <option value="">— выберите —</option>
                  {годные.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="co-date">Дата поставки</label>
                <input
                  id="co-date"
                  type="date"
                  value={дата}
                  onChange={(e) => setДата(e.target.value)}
                  required
                />
                <span className="hint-inline">
                  Номер программа присвоит сама
                </span>
              </div>

              <div className="field full">
                <label htmlFor="co-title">Название поставки</label>
                <input
                  id="co-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`Поставка от ${formatDate(дата)}`}
                />
                <span className="hint-inline">
                  Необязательно. Так, как её называет клиент, — чтобы говорить об
                  одном и том же
                </span>
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn primary"
            disabled={create.isPending || !clientId || !дата}
          >
            {create.isPending ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  )
}
